import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { analyticPanelApiRequests } from 'utils/api-requests/analyticPanel';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import {
    AnalyticPanelCalculation,
    AnalyticPanelCosts,
    AnalyticPanelFilter,
    AnalyticPanelFlip,
    AnalyticPanelState,
    CALCULATION_FIELD_TO_PARAM_ENUM,
    CALCULATION_FIELD_TO_RESPONSE_KEY,
    CALCULATION_PERCENT_PAIRS,
    FIELD_TO_PARAM_ENUM,
    initialAnalyticPanelState,
    InvestmentObject
} from 'utils/types/AnalyticPanelModule';

import Indicator from 'components/atom/Indicator';
import { activeNotification } from 'components/functions/activeNotification';

export interface AnalyticPanelContextType {
    filtersAnalyticPanelState: AnalyticPanelState;
    showReport: boolean;
    setShowReport: (show: boolean) => void;
    handleChange: (fieldName: keyof AnalyticPanelState, value: string | number | null) => void;
    handleFilterChange: (fieldName: keyof AnalyticPanelFilter, value: string | null) => void;
    handleCalculationChange: (fieldName: keyof AnalyticPanelCalculation, value: string | null) => void;
    handleFlipChange: (fieldName: keyof AnalyticPanelFlip, value: string | null) => void;
    handleSetFilterValues: (id: number) => Promise<void>;
    handleDeleteFilter: (id: number) => Promise<void>;
    getSavedFilters: (id?: number) => Promise<void>;
    clearFilter: () => void;
    saveFilter: (name: string) => Promise<void>;
    calculateInvestment: () => Promise<void>;
    recalculateOnBlur: () => Promise<void>;
    recalculateWithCalculation: (calculationUpdates: Partial<AnalyticPanelCalculation>) => Promise<void>;
    recalculateWithFlipStage: (fieldName: keyof AnalyticPanelFlip, value: string) => Promise<void>;
    updateTotalField: (fieldName: keyof AnalyticPanelFilter, totalValue: string) => Promise<void>;
    updateCalculationAmountField: (fieldName: keyof AnalyticPanelCalculation, amountValue: string) => Promise<void>;
}

const values: AnalyticPanelContextType = {
    filtersAnalyticPanelState: {} as AnalyticPanelState,
    showReport: false,
    setShowReport: () => {},
    handleChange: () => {},
    handleFilterChange: () => {},
    handleCalculationChange: () => {},
    handleFlipChange: () => {},
    handleSetFilterValues: () => Promise.resolve(),
    handleDeleteFilter: () => Promise.resolve(),
    getSavedFilters: () => Promise.resolve(),
    clearFilter: () => {},
    saveFilter: () => Promise.resolve(),
    calculateInvestment: () => Promise.resolve(),
    recalculateOnBlur: () => Promise.resolve(),
    recalculateWithCalculation: () => Promise.resolve(),
    recalculateWithFlipStage: () => Promise.resolve(),
    updateTotalField: () => Promise.resolve(),
    updateCalculationAmountField: () => Promise.resolve()
};

export const AnalyticPanelContext = createContext(values);

const PERCENTAGE_AMOUNT = '50';

const withCalculationDefaults = (calculation: AnalyticPanelCalculation | null | undefined): AnalyticPanelCalculation | null => {
    if (!calculation) return null;

    return {
        ...calculation,
        flipperCapitalPercent: calculation.flipperCapitalPercent ?? PERCENTAGE_AMOUNT,
        investorCapitalPercent: calculation.investorCapitalPercent ?? PERCENTAGE_AMOUNT,
        flipperProfitPercent: calculation.flipperProfitPercent ?? PERCENTAGE_AMOUNT,
        investorProfitPercent: calculation.investorProfitPercent ?? PERCENTAGE_AMOUNT
    };
};

export const AnalyticPanelProvider = ({ children }: { children: React.ReactNode }) => {
    const [filtersAnalyticPanelState, setFiltersAnalyticPanelState] = useState<AnalyticPanelState>(initialAnalyticPanelState);
    const [showReport, setShowReport] = useState(false);
    const recalculationGenRef = useRef(0);

    useEffect(() => {
        const fetchInitialData = async () => {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

            const defaultData = await analyticPanelApiRequests.getDefaultFilter();
            const savedFilters = await analyticPanelApiRequests.getSavedFilters();

            const calculationWithDefaults = withCalculationDefaults(defaultData?.calculation);

            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: defaultData?.purchaseDate || null,
                saleDate: defaultData?.saleDate || null,
                investmentTime: defaultData?.investmentTime || null,
                filter: defaultData?.filter || null,
                calculation: calculationWithDefaults,
                flip: defaultData?.flip || null,
                costs: defaultData?.costs || null,
                savedFilters,
                isLoading: false
            }));
        };

        fetchInitialData();
    }, []);

    const getSavedFilters = useCallback(async (id?: number) => {
        const savedFilters = await analyticPanelApiRequests.getSavedFilters();

        setFiltersAnalyticPanelState((state) => ({
            ...state,
            savedFilters
        }));

        if (id) {
            await handleSetFilterValues(id);
        }
    }, []);

    const handleSetFilterValues = async (id: number) => {
        const filterData = await analyticPanelApiRequests.getFilter(id);

        if (filterData) {
            const calculationWithDefaults = withCalculationDefaults(filterData.calculation);

            setFiltersAnalyticPanelState((state) => ({
                ...state,
                filterId: filterData.id,
                filterName: filterData.name || '',
                purchaseDate: filterData.purchaseDate,
                saleDate: filterData.saleDate,
                investmentTime: filterData.investmentTime,
                purchasePrice: filterData.price || '',
                salePrice: filterData.salePrice,
                address: filterData.address || '',
                floor: filterData.floor,
                area: filterData.area,
                rooms: filterData.rooms,
                filter: filterData.filter,
                calculation: calculationWithDefaults,
                flip: filterData.flip,
                costs: filterData.costs
            }));
        } else {
            setFiltersAnalyticPanelState((state) => ({
                ...state,
                filterName: '',
                filterId: null
            }));
        }
    };

    const handleDeleteFilter = useCallback(async (filterId: number) => {
        await analyticPanelApiRequests.deleteFilter(filterId);

        const defaultData = await analyticPanelApiRequests.getDefaultFilter();
        const calculationWithDefaults = withCalculationDefaults(defaultData?.calculation);

        setFiltersAnalyticPanelState((prev) => ({
            ...initialAnalyticPanelState,
            savedFilters: prev.savedFilters,
            purchaseDate: defaultData?.purchaseDate || null,
            saleDate: defaultData?.saleDate || null,
            investmentTime: defaultData?.investmentTime || null,
            filter: defaultData?.filter || null,
            calculation: calculationWithDefaults,
            flip: defaultData?.flip || null,
            costs: defaultData?.costs || null
        }));

        getSavedFilters();
    }, [getSavedFilters]);

    const clearFilter = useCallback(async () => {
        const defaultData = await analyticPanelApiRequests.getDefaultFilter();

        const calculationWithDefaults = withCalculationDefaults(defaultData?.calculation);

        setFiltersAnalyticPanelState((prev) => ({
            ...initialAnalyticPanelState,
            savedFilters: prev.savedFilters,
            purchaseDate: defaultData?.purchaseDate || null,
            saleDate: defaultData?.saleDate || null,
            investmentTime: defaultData?.investmentTime || null,
            filter: defaultData?.filter || null,
            calculation: calculationWithDefaults,
            flip: defaultData?.flip || null,
            costs: defaultData?.costs || null
        }));

        await getSavedFilters();
    }, [getSavedFilters]);

    const handleChange = useCallback((fieldName: keyof AnalyticPanelState, value: string | number | null) => {
        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    }, []);

    const handleFilterChange = useCallback((fieldName: keyof AnalyticPanelFilter, value: string | null) => {
        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            filter: prev.filter ? { ...prev.filter, [fieldName]: value } : null
        }));
    }, []);

    const handleCalculationChange = useCallback((fieldName: keyof AnalyticPanelCalculation, value: string | null) => {
        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            calculation: prev.calculation ? { ...prev.calculation, [fieldName]: value } : null
        }));
    }, []);

    const handleFlipChange = useCallback((fieldName: keyof AnalyticPanelFlip, value: string | null) => {
        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            flip: prev.flip
                ? { ...prev.flip, [fieldName]: value }
                : { firstStage: null, secondStage: null, thirdStage: null, finalCosts: null, unusedBudget: null, [fieldName]: value }
        }));
    }, []);

    const buildInvestmentObject = useCallback((overrides?: Partial<InvestmentObject>): InvestmentObject => ({
        id: filtersAnalyticPanelState.filterId,
        name: filtersAnalyticPanelState.filterName || null,
        price: filtersAnalyticPanelState.purchasePrice || null,
        salePrice: filtersAnalyticPanelState.salePrice,
        purchaseDate: filtersAnalyticPanelState.purchaseDate,
        saleDate: filtersAnalyticPanelState.saleDate,
        address: filtersAnalyticPanelState.address || null,
        investmentTime: filtersAnalyticPanelState.investmentTime,
        area: filtersAnalyticPanelState.area,
        rooms: filtersAnalyticPanelState.rooms,
        floor: filtersAnalyticPanelState.floor !== null && filtersAnalyticPanelState.floor !== ''
            ? filtersAnalyticPanelState.floor
            : null,
        filter: filtersAnalyticPanelState.filter || {} as AnalyticPanelFilter,
        calculation: filtersAnalyticPanelState.calculation || {} as AnalyticPanelCalculation,
        flip: filtersAnalyticPanelState.flip || { firstStage: null, secondStage: null, thirdStage: null, finalCosts: null, unusedBudget: null },
        costs: filtersAnalyticPanelState.costs || {} as AnalyticPanelCosts,
        ...overrides
    }), [filtersAnalyticPanelState]);

    const saveFilter = useCallback(async (name: string) => {
        if (!name.trim()) {
            activeNotification('Brak nazwy', 'Nie można zapisać filtra bez nazwy', 'warning');

            return;
        }

        if (!filtersAnalyticPanelState.purchasePrice) {
            activeNotification('Brak ceny zakupu', 'Wypełnij pole "Cena zakupu mieszkania" przed zapisaniem', 'warning');

            return;
        }

        if (!filtersAnalyticPanelState.area) {
            activeNotification('Brak metrażu', 'Wypełnij pole "Metraż" przed zapisaniem', 'warning');

            return;
        }

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const currentFilterId = filtersAnalyticPanelState.filterId;

        const payload = buildInvestmentObject({ name, id: currentFilterId });

        const response = currentFilterId
            ? await analyticPanelApiRequests.updateFilter(payload)
            : await analyticPanelApiRequests.createFilter(payload);

        if (response) {
            await getSavedFilters(response.id ?? undefined);
        }

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
    }, [filtersAnalyticPanelState, getSavedFilters]);

    const calculateInvestment = useCallback(async () => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);
        const hasPrice = price > 0;
        const hasArea = area > 0;

        const hasAnyField = hasPrice || hasArea;

        if (!hasPrice && hasAnyField) {
            activeNotification('Brak ceny zakupu', 'Wypełnij pole "Cena zakupu"', 'warning');

            return;
        }

        if (!hasArea && hasAnyField) {
            activeNotification('Brak metrażu', 'Wypełnij pole "Powierzchnia"', 'warning');

            return;
        }

        if (!hasPrice && !hasArea) {
            return;
        }

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(buildInvestmentObject());

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: apiResults.purchaseDate,
                saleDate: apiResults.saleDate,
                salePrice: apiResults.salePrice,
                investmentTime: apiResults.investmentTime,
                filter: apiResults.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    const recalculateOnBlur = useCallback(async () => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);

        if (price <= 0 || area <= 0) {
            return;
        }

        recalculationGenRef.current += 1;
        const gen = recalculationGenRef.current;

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(buildInvestmentObject());

        if (gen !== recalculationGenRef.current) return;

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: apiResults.purchaseDate,
                saleDate: apiResults.saleDate,
                salePrice: apiResults.salePrice,
                investmentTime: apiResults.investmentTime,
                filter: apiResults.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    const updateTotalField = useCallback(async (fieldName: keyof AnalyticPanelFilter, totalValue: string) => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);
        const salePrice = stringToNumberOrZero(filtersAnalyticPanelState.salePrice);

        if (price <= 0 || area <= 0) {
            return;
        }

        const paramEnum = FIELD_TO_PARAM_ENUM[fieldName];

        if (!paramEnum) {
            return;
        }

        recalculationGenRef.current += 1;
        const gen = recalculationGenRef.current;

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const result = await analyticPanelApiRequests.calculateByParam({
            param: paramEnum,
            area,
            price,
            salePrice,
            projectCapital: stringToNumberOrZero(filtersAnalyticPanelState.calculation?.projectCapital),
            estimatedProfitNetto: stringToNumberOrZero(filtersAnalyticPanelState.calculation?.estimatedProfitNetto),
            value: stringToNumberOrZero(totalValue)
        });

        if (gen !== recalculationGenRef.current) return;

        if (!result || result.value == null) {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        const perUnitValue = String(result.value);

        const updatedFilter = filtersAnalyticPanelState.filter
            ? { ...filtersAnalyticPanelState.filter, [fieldName]: perUnitValue }
            : null;

        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            filter: updatedFilter
        }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(
            buildInvestmentObject({
                filter: updatedFilter || {} as AnalyticPanelFilter
            })
        );

        if (gen !== recalculationGenRef.current) return;

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                filter: apiResults.filter
                    ? { ...apiResults.filter, [fieldName]: perUnitValue }
                    : prev.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    const recalculateWithCalculation = useCallback(async (calculationUpdates: Partial<AnalyticPanelCalculation>) => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);

        if (price <= 0 || area <= 0) {
            return;
        }

        const updatedCalculation = filtersAnalyticPanelState.calculation
            ? { ...filtersAnalyticPanelState.calculation, ...calculationUpdates }
            : calculationUpdates as AnalyticPanelCalculation;

        recalculationGenRef.current += 1;
        const gen = recalculationGenRef.current;

        setFiltersAnalyticPanelState((prev) => ({
            ...prev,
            calculation: updatedCalculation,
            isLoading: true
        }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(
            buildInvestmentObject({ calculation: updatedCalculation })
        );

        if (gen !== recalculationGenRef.current) return;

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: apiResults.purchaseDate,
                saleDate: apiResults.saleDate,
                salePrice: apiResults.salePrice,
                investmentTime: apiResults.investmentTime,
                filter: apiResults.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    const recalculateWithFlipStage = useCallback(async (fieldName: keyof AnalyticPanelFlip, value: string) => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);

        if (price <= 0 || area <= 0) {
            return;
        }

        const updatedFlip: AnalyticPanelFlip = filtersAnalyticPanelState.flip
            ? { ...filtersAnalyticPanelState.flip, [fieldName]: value }
            : { firstStage: null, secondStage: null, thirdStage: null, finalCosts: null, unusedBudget: null, [fieldName]: value };

        recalculationGenRef.current += 1;
        const gen = recalculationGenRef.current;

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(
            buildInvestmentObject({ flip: updatedFlip })
        );

        if (gen !== recalculationGenRef.current) return;

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: apiResults.purchaseDate,
                saleDate: apiResults.saleDate,
                salePrice: apiResults.salePrice,
                investmentTime: apiResults.investmentTime,
                filter: apiResults.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    const updateCalculationAmountField = useCallback(async (fieldName: keyof AnalyticPanelCalculation, amountValue: string) => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const area = stringToNumberOrZero(filtersAnalyticPanelState.area);
        const salePrice = stringToNumberOrZero(filtersAnalyticPanelState.salePrice);

        if (price <= 0 || area <= 0) {
            return;
        }

        const enteredAmount = stringToNumberOrZero(amountValue);
        const isCapitalField = fieldName === 'flipperCapitalPercent' || fieldName === 'investorCapitalPercent';
        const isProfitField = fieldName === 'flipperProfitPercent' || fieldName === 'investorProfitPercent';

        if (isCapitalField) {
            const maxCapital = stringToNumberOrZero(filtersAnalyticPanelState.calculation?.projectCapital);

            if (maxCapital > 0 && enteredAmount > maxCapital) {
                activeNotification('Nieprawidłowa kwota', `Kwota nie może przekraczać całkowitego kapitału projektu (${maxCapital.toLocaleString('pl-PL')} zł)`, 'warning');

                return;
            }
        }

        if (isProfitField) {
            const maxProfit = stringToNumberOrZero(filtersAnalyticPanelState.calculation?.estimatedProfitNetto);

            if (maxProfit > 0 && enteredAmount > maxProfit) {
                activeNotification('Nieprawidłowa kwota', `Kwota nie może przekraczać całkowitego zysku (${maxProfit.toLocaleString('pl-PL')} zł)`, 'warning');

                return;
            }
        }

        const paramEnum = CALCULATION_FIELD_TO_PARAM_ENUM[fieldName];

        if (!paramEnum) {
            return;
        }

        recalculationGenRef.current += 1;
        const gen = recalculationGenRef.current;

        setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: true }));

        const result = await analyticPanelApiRequests.calculateByParam({
            param: paramEnum,
            area,
            price,
            salePrice,
            projectCapital: stringToNumberOrZero(filtersAnalyticPanelState.calculation?.projectCapital),
            estimatedProfitNetto: stringToNumberOrZero(filtersAnalyticPanelState.calculation?.estimatedProfitNetto),
            value: stringToNumberOrZero(amountValue)
        });

        if (gen !== recalculationGenRef.current) return;

        if (!result) {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        const responseKey = CALCULATION_FIELD_TO_RESPONSE_KEY[fieldName];
        const percentStr = responseKey ? result[responseKey] : undefined;

        if (percentStr === undefined) {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        const percent = parseFloat(percentStr as string);
        const pairedField = CALCULATION_PERCENT_PAIRS[fieldName];
        const pairedResponseKey = pairedField ? CALCULATION_FIELD_TO_RESPONSE_KEY[pairedField] : undefined;
        const pairedPercentStr = pairedResponseKey ? result[pairedResponseKey] : undefined;
        const complementPercent = pairedPercentStr !== undefined ? parseFloat(pairedPercentStr as string) : undefined;

        const calculationUpdates: Partial<AnalyticPanelCalculation> = {
            [fieldName]: percent.toString(),
            ...pairedField && complementPercent !== undefined ? { [pairedField]: complementPercent.toString() } : {}
        };

        const updatedCalculation = filtersAnalyticPanelState.calculation
            ? { ...filtersAnalyticPanelState.calculation, ...calculationUpdates }
            : calculationUpdates as AnalyticPanelCalculation;

        setFiltersAnalyticPanelState((prev) => ({ ...prev, calculation: updatedCalculation }));

        const apiResults = await analyticPanelApiRequests.calculateInvestment(
            buildInvestmentObject({ calculation: updatedCalculation })
        );

        if (gen !== recalculationGenRef.current) return;

        if (apiResults) {
            setFiltersAnalyticPanelState((prev) => ({
                ...prev,
                purchaseDate: apiResults.purchaseDate,
                saleDate: apiResults.saleDate,
                salePrice: apiResults.salePrice,
                investmentTime: apiResults.investmentTime,
                filter: apiResults.filter,
                calculation: apiResults.calculation,
                flip: apiResults.flip,
                costs: apiResults.costs,
                isLoading: false
            }));
        } else {
            setFiltersAnalyticPanelState((prev) => ({ ...prev, isLoading: false }));
        }
    }, [filtersAnalyticPanelState, buildInvestmentObject]);

    return (
        <AnalyticPanelContext.Provider
            value={{
                filtersAnalyticPanelState,
                showReport,
                setShowReport,
                handleChange,
                handleFilterChange,
                handleCalculationChange,
                handleFlipChange,
                handleSetFilterValues,
                handleDeleteFilter,
                getSavedFilters,
                clearFilter,
                saveFilter,
                calculateInvestment,
                recalculateOnBlur,
                recalculateWithCalculation,
                recalculateWithFlipStage,
                updateTotalField,
                updateCalculationAmountField
            }}
        >
            {filtersAnalyticPanelState.isLoading && <Indicator />}
            {children}
        </AnalyticPanelContext.Provider>
    );
};

export default AnalyticPanelContext;
